"use client";

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
  onAuthStateChanged,
  Auth,
  User, 
  getAdditionalUserInfo
} from 'firebase/auth';
import { getAuthInstance } from '@/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, Loader2, Mail } from 'lucide-react';
import { apiEndpoint } from '@/lib/config';

export default function SignUpPage() {
  const router = useRouter();
  
  // Form inputs
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  
  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  
  // Auth state
  const [auth, setAuth] = useState<Auth | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Initialize Firebase auth
  useEffect(() => {
    const initialize = async () => {
      try {
        const authInstance = await getAuthInstance();
        setAuth(authInstance);
        
        const unsubscribe = onAuthStateChanged(authInstance, (user) => {
          setCurrentUser(user);
          setIsInitializing(false);
          
          // If user is already logged in and verified, redirect to dashboard
          if (user?.emailVerified) {
            router.push('/dashboard');
          }
        });
        
        return () => unsubscribe();
      } catch (error) {
        console.error("Firebase initialization failed:", error);
        setErrorMessage("Kunde inte ansluta till autentiseringstjänsten. Försök igen senare.");
        setIsInitializing(false);
      }
    };
    
    initialize();
  }, [router]);

  // Create user in adminUsers collection
  const createUserDocument = async (userId: string, isGoogleUser: boolean = false) => {
    try {
      const response = await fetch(apiEndpoint('/api/create-user'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          email,
          displayName: displayName || email.split('@')[0],
          activeDomains: 0,
          maxDomains: 0,
          stripeSubscriptionId: '',
          verified: false
        }),
      });
      
      if (!response.ok) {
        throw new Error('Misslyckades med att skapa användardokument');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Fel vid skapande av användardokument:', error);
      throw error;
    }
  };

  // Handle Google signup
  const handleGoogleSignUp = async () => {
    if (!auth) return;
    
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      const additionalInfo = getAdditionalUserInfo(result);
      const isNewUser = additionalInfo?.isNewUser;
      
      if (isNewUser) {
        await createUserDocument(user.uid, true);
        
        setSuccessMessage("Konto skapat framgångsrikt!");
        
        // Short delay then redirect to dashboard
        setTimeout(() => {
          router.push('/dashboard');
        }, 2000);
      } else {
        // Existing user, redirect to dashboard
        router.push('/dashboard');
      }
    } catch (error: any) {
      console.error('Google-registreringsfel:', error);
      setErrorMessage(getFirebaseErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle email/password signup
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!auth) return;
    
    // Validate form
    if (password !== confirmPassword) {
      setErrorMessage("Lösenorden matchar inte");
      return;
    }
    
    if (password.length < 6) {
      setErrorMessage("Lösenordet måste vara minst 6 tecken");
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Send verification email
      await sendEmailVerification(user, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false
      });
      
      await createUserDocument(user.uid, false);
      
      setVerificationSent(true);
      setSuccessMessage("Konto skapat! Kontrollera din e-post för att verifiera din adress innan du loggar in.");
    } catch (error: any) {
      console.error('Registreringsfel:', error);
      setErrorMessage(getFirebaseErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  // Format error messages from Firebase
  const getFirebaseErrorMessage = (error: any): string => {
    switch (error.code) {
      case 'auth/invalid-email':
        return 'Ogiltigt e-postadressformat.';
      case 'auth/user-disabled':
        return 'Detta konto har inaktiverats.';
      case 'auth/email-already-in-use':
        return 'Ett konto med denna e-post finns redan.';
      case 'auth/weak-password':
        return 'Lösenordet är för svagt (minst 6 tecken krävs).';
      case 'auth/operation-not-allowed':
        return 'Kontoskapande är inte aktiverat.';
      case 'auth/popup-closed-by-user':
        return 'Registreringsfönstret stängdes innan processen var klar.';
      case 'auth/account-exists-with-different-credential':
        return 'Ett konto finns redan med denna e-post med en annan inloggningsmetod.';
      default:
        return `Ett fel uppstod: ${error.message}`;
    }
  };

  // Loading state
  if (isInitializing) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Laddar autentisering...</p>
        </div>
      </div>
    );
  }

  // Email verification sent view
  if (verificationSent) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-xl text-center">Verifierings-e-post skickad</CardTitle>
            <CardDescription className="text-center">
              Kontrollera din inkorg för att slutföra registreringen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-center">
              <Mail className="h-16 w-16 text-primary" />
            </div>
            <Alert variant="default" className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertTitle>E-post skickad framgångsrikt</AlertTitle>
              <AlertDescription>
                Vi har skickat en verifieringslänk till <span className="font-semibold">{email}</span>. 
                Kontrollera din inkorg och klicka på länken för att verifiera din e-postadress.
              </AlertDescription>
            </Alert>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              När du har verifierat kan du logga in på ditt konto och börja hantera dina domäner.
            </p>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button variant="outline" className="w-full" onClick={() => router.push('/login')}>
              Gå till inloggning
            </Button>
            <p className="text-sm text-gray-500 text-center">
              Fick inte e-posten? Kontrollera din skräppostmapp eller kontakta support.
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Normal signup form
  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Skapa ditt konto</CardTitle>
          <CardDescription className="text-center">
            Registrera dig för att hantera din cookie-efterlevnad
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSignUp} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="displayName">Namn</Label>
              <Input
                id="displayName"
                type="text"
                placeholder="Ditt namn"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="rounded-md"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                type="email"
                placeholder="din-email@exempel.se"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-md"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Lösenord</Label>
              <Input
                id="password"
                type="password"
                placeholder="Skapa ett lösenord"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="rounded-md"
              />
              <p className="text-xs text-gray-500">Måste vara minst 6 tecken</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Bekräfta lösenord</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Bekräfta ditt lösenord"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="rounded-md"
              />
            </div>

            {errorMessage && (
              <Alert variant="default" className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Fel</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
            
            {successMessage && !verificationSent && (
              <Alert variant="default" className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription>{successMessage}</AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || !auth}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Skapar konto...
                </>
              ) : (
                'Skapa konto'
              )}
            </Button>
          </form>

          <div className="mt-6 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Eller fortsätt med
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            type="button"
            className="w-full mt-4 flex items-center justify-center gap-2"
            onClick={handleGoogleSignUp}
            disabled={isLoading || !auth}
          >
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"></path>
                <path fill="#34A853" d="M6.306 14.691L11.96 18.938C13.48 14.816 18.272 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"></path>
                <path fill="#FBBC05" d="M24 36c5.728 0 10.52-3.184 12.481-7.734l-5.657-4.244C29.163 27.182 26.718 28 24 28c-5.728 0-10.52-3.184-12.481-7.734L6.306 25.168C9.656 31.663 16.318 36 24 36z"></path>
                <path fill="#EA4335" d="M43.611 20.083H24v8h11.303c-.792 2.237-2.231 4.16-4.082 5.571l5.657 4.244C40.497 34.011 44 27.824 44 24c0-1.341-.138-2.65-.389-3.917z"></path>
              </svg>
            )}
            Registrera dig med Google
          </Button>
        </CardContent>
        
        <CardFooter className="flex flex-col space-y-2">
          <div className="text-sm text-center">
            Har du redan ett konto?{' '}
            <Link href="/login" className="text-primary hover:underline font-semibold">
              Logga in
            </Link>
          </div>
          <div className="text-sm text-center">
            <Link href="/auth/password-reset" className="text-gray-600 hover:underline">
              Glömt ditt lösenord?
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}